import * as VTable from '@visactor/vtable'
import { InputEditor, ListEditor } from '@visactor/vtable-editors'
import { FIELD_REGISTRY, enumValueToText, getEnumLabels, type EnumFieldDef } from './fieldRegistry'

/**
 * 注册所有 VTable 编辑器（在应用启动时调用一次）
 */
export const registerVTableEditors = () => {
  class EnumListEditor extends ListEditor {
    private readonly valuesList: string[]
    private readonly mapValue: (value: unknown) => string

    constructor(values: string[], mapValue: (value: unknown) => string) {
      super({ values })
      this.valuesList = values
      this.mapValue = mapValue
    }

    onStart(params: { value?: unknown }) {
      const currentValue = params?.value
      const mappedValue =
        typeof currentValue === 'string' && this.valuesList.includes(currentValue)
          ? currentValue
          : this.mapValue(currentValue)
      super.onStart({ ...(params as object), value: mappedValue } as unknown as Parameters<ListEditor['onStart']>[0])

      // 选择即提交：用户选中枚举项后自动结束编辑（ListEditor 基类的 _bindSelectChangeEvent 是空实现且未被调用）
      this.element?.addEventListener('change', () => {
        setTimeout(() => this.successCallback?.(), 0)
      })
    }

    // 重写 onEnd，添加空值检查
    onEnd() {
      if (this.container && this.element) {
        try {
          super.onEnd()
        } catch (error) {
          console.warn('EnumListEditor.onEnd 清理失败（可能已被移除）:', error)
        }
      }
    }
  }

  const toNumberOrNull = (val: unknown) => {
    if (val === null || val === undefined || val === '') return null
    const num = Number(val)
    return Number.isNaN(num) ? null : num
  }

  // 自定义文本编辑器（支持回车提交）
  class CustomInputEditor extends InputEditor {
    private endEditCallback?: () => void

    onStart(params: Parameters<InputEditor['onStart']>[0]) {
      // 先保存 endEdit 回调
      this.endEditCallback = params.endEdit

      // 调用父类 onStart
      super.onStart(params)

      // 监听回车键
      const enterHandler = (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          e.stopPropagation()
          // 调用 endEdit 触发保存流程
          this.endEditCallback?.()
        }
      }

      // 在 super.onStart 之后添加事件监听，避免被覆盖
      setTimeout(() => {
        this.element?.addEventListener('keydown', enterHandler, true) // 使用捕获阶段
        this.eventHandlers.push({ type: 'keydown', handler: enterHandler as EventListener })
      }, 0)
    }
  }

  // 文本编辑器（自定义，支持回车提交）
  VTable.register.editor('text-editor', new CustomInputEditor({}))

  // 日期时间编辑器：复用文本编辑器，用户直接输入 YYYY-MM-DD HH:mm:ss 格式
  // 校验在 convertEditorValueToDbValue 中完成，无效格式由后端拒收
  VTable.register.editor('date-editor', new CustomInputEditor({}))

  // ========== 枚举编辑器（从注册表循环注册） ==========
  for (const [field, def] of Object.entries(FIELD_REGISTRY)) {
    if (def.kind !== 'enum') continue
    const enumDef = def as EnumFieldDef
    VTable.register.editor(
      enumDef.editorName,
      new EnumListEditor(
        getEnumLabels(field),
        (val) => enumValueToText(field, toNumberOrNull(val))
      )
    )
  }

  // 杂费说明编辑器（弹 Modal，不做内联编辑）
  // dictType 直接绑定到编辑器实例，避免依赖 currentField 全局变量的时序问题
  // （editCellTrigger: 'doubleclick' 导致 VTable 自动启动编辑器早于 onDblClickCell 事件）
  class MiscFeeDescEditor {
    container!: HTMLElement
    element!: HTMLElement
    private currentValue = ''
    private newValue: string | null = null
    private endEditCallback?: () => void
    private isModalOpen = false
    private dictType: 'misc' | 'car'

    constructor(dictType: 'misc' | 'car') {
      this.dictType = dictType
    }

    beginEditing(
      _container: HTMLElement,
      _referencePosition: { rect: { left: number; top: number; width: number; height: number } }
    ) {
      // VTable 需要 container，但我们不渲染任何内联元素
    }

    onStart(params: { value?: unknown; container: HTMLElement; endEdit: () => void }) {
      this.container = params.container
      this.currentValue = params.value != null ? String(params.value) : ''
      this.newValue = null
      this.endEditCallback = params.endEdit
      this.isModalOpen = true

      // 创建一个隐藏占位元素（VTable 要求 editor 有 DOM）
      this.element = document.createElement('div')
      this.element.style.display = 'none'
      this.container.appendChild(this.element)

      // 通过全局回调通知 React 组件打开 Modal，直接传递 dictType
      miscFeeEditorBridge.open?.(this.currentValue, this.dictType, (value: string | null) => {
        if (value !== null) {
          this.newValue = value
        }
        this.isModalOpen = false
        this.endEditCallback?.()
      })
    }

    getValue() {
      return this.newValue !== null ? this.newValue : this.currentValue
    }

    onEnd() {
      this.isModalOpen = false
      if (this.element?.parentNode) {
        this.element.parentNode.removeChild(this.element)
      }
    }

    // Modal 打开期间，所有点击都视为编辑器内部元素，阻止 VTable 提前结束编辑
    isEditorElement() {
      return this.isModalOpen
    }
  }

  VTable.register.editor('misc-fee-desc-editor', new MiscFeeDescEditor('misc'))
  VTable.register.editor('car-misc-fee-desc-editor', new MiscFeeDescEditor('car'))

  console.log('[VTable] 编辑器注册完成')
}

/**
 * 杂费编辑器桥接：VTable editor class → React Modal
 * open: 由编辑器调用，通知 React 打开 Modal
 * setOpenHandler: 由 React 组件调用，注册回调
 */
export const miscFeeEditorBridge = {
  open: null as ((currentValue: string, dictType: 'misc' | 'car', onDone: (value: string | null) => void) => void) | null,
}

/**
 * 动态注册箱型编辑器（异步获取箱型列表后调用）
 */
export const registerBoxEditor = (containerTypes: string[]) => {
  VTable.register.editor('box-editor', new ListEditor({ values: containerTypes }))
}
